<script>
import Layout from '@layouts/main.vue';
import AdminPanel from '@components/admin-panel/admin-panel.vue';
import CheckoutSummaryBlock from '@components/checkout/checkout-summary-block.vue';
import CardProductItem from '@components/checkout/card-product-item.vue';
import EmptyCardProductItem from '@components/checkout/empty-card-product-item.vue';
import UseLargeScreen from '@components/use-large-screen.vue';
import { gridMixin } from '@mixins/grid-mixin';
import { helperMixin } from '@mixins/helper-mixin';
import {
  trainerComputed,
  trainerMethods,
  authMethods,
  adminPanelComputed,
  adminPanelMethods,
  clientComputed,
  clientMethods,
  authComputed,
} from '@state/helpers';
import ProductForm from '@components/forms/product-form.vue';
import ProductDetail from '@components/checkout/card-product-detail.vue';
import CouponForm from '@components/forms/coupon-form.vue';
import cloneDeep from 'lodash/cloneDeep';
import _isEmpty from 'lodash/isEmpty';
import has from 'lodash/has';

import { breakpointsMixin } from '@mixins/breakpoints-mixin.js';
import { adminPanelMixin } from '@mixins/admin-panel-mixin';

export default {
  page: {
    title: 'Settings',
    meta: [{ name: 'description', content: 'The Settings page.' }],
  },
  components: {
    AdminPanel,
    Layout,
    ProductForm,
    CouponForm,
    CheckoutSummaryBlock,
    CardProductItem,
    EmptyCardProductItem,
    ProductDetail,
    UseLargeScreen,
  },
  computed: {
    ...adminPanelComputed,
    ...trainerComputed,
    ...clientComputed,
    ...authComputed,
    groupedProducts() {
      let products = [];

      if (
        this.user.trainer.myProducts &&
        this.user.trainer.myProducts.length > 0
      ) {
        products = cloneDeep(this.user.trainer.myProducts[0].plans);
      } else {
      }
      products.unshift({ id: 'isNew' });
      return this.getGridGroups(products, 4);
    },
    blankCards() {
      const plans = this.user.trainer.myProducts[0].plans;
      const cardsCount = 3 - plans.length;
      const blankCards = [];
      for (let i = 0; i < cardsCount; i++) {
        blankCards.push({ _id: Math.random() });
      }
      return blankCards;
    },
    monthsActive() {
      let count = 0;
      if (
        this.user.trainer.myProducts &&
        this.user.trainer.myProducts.length > 0 &&
        this.user.trainer.myProducts[0].plans &&
        this.user.trainer.myProducts[0].plans.length > 0
      ) {
        const products = this.user.trainer.myProducts[0].plans;
        products.forEach((product) => {
          if (
            product.type === 'recurring' &&
            product.recurring.interval === 'week'
          ) {
            count += parseInt(product.recurring.interval_count);
          } else if (
            product.type === 'recurring' &&
            product.recurring.interval === 'month'
          ) {
            count += parseInt(product.recurring.interval_count) * 4;
          } else if (
            product.type === 'one_time' &&
            product.metadata &&
            product.metadata.access_length
          ) {
            count += parseInt(product.metadata.access_length);
          }
        });
      }
      return count / 4;
    },
    totalWeeksPaid() {
      let count = 0;
      if (this.joinedClientsProducts && this.joinedClientsProducts.length > 0) {
        this.joinedClientsProducts.forEach((product) => {
          const activeSubcribers =
            product.subscribers.length > 0
              ? product.subscribers.filter((subscriber) => {
                  if (
                    has(subscriber.profile, 'subscribed') &&
                    subscriber.profile.subscribed &&
                    has(subscriber.profile, 'subscribed_plan') &&
                    subscriber.profile.subscribed_plan &&
                    has(subscriber.profile, 'end_date') &&
                    subscriber.profile.end_date &&
                    has(subscriber.profile, 'end_date') &&
                    subscriber.profile.end_date
                  ) {
                    return subscriber;
                  }
                }).length
              : [];

          if (
            product.product.type === 'recurring' &&
            product.product.recurring.interval === 'week'
          ) {
            count +=
              parseInt(product.product.recurring.interval_count) *
              activeSubcribers;
          } else if (
            product.product.type === 'recurring' &&
            product.product.recurring.interval === 'month'
          ) {
            count +=
              parseInt(product.product.recurring.interval_count) *
              4 *
              activeSubcribers;
          } else if (
            product.product.type === 'one_time' &&
            product.product.metadata &&
            product.product.metadata.access_length
          ) {
            count +=
              parseInt(product.product.metadata.access_length) *
              activeSubcribers;
          }
        });
      }
      return count;
    },
    activeProducts() {
      return this.user.trainer.myProducts.length > 0
        ? this.user.trainer.myProducts[0].plans.length
        : 0;
    },
  },
  mixins: [adminPanelMixin, gridMixin, helperMixin, breakpointsMixin],
  props: {
    user: {
      type: Object,
      required: true,
      default: () => {},
    },
  },
  data: function() {
    return {
      removingCoupon: null,
      removingPrice: null,
    };
  },
  async mounted() {
    if (this.trainerId && this.allClient.length === 0) {
      try {
        await this.getAllClient({ trainerId: this.trainerId });
      } catch (e) {
        console.log(e);
      }
    }

    if (this.currentUser.trainer.onboardUrl) {
      // Not fully onboarded. Ask to redirect
      this.$bvModal.show('onboarding-modal');
    }
  },
  methods: {
    ...adminPanelMethods,
    ...trainerMethods,
    ...authMethods,
    ...clientMethods,
    empty(value) {
      return _isEmpty(value);
    },
    async showAdminPanel(component, data = null, size = '') {
      await this.setPanelContent(this.getPanelContent(component, data, size));
      this.setShowAdminPanel();
    },
    buildProductTitle(product) {
      return (
        '$' +
        (product.plans.length > 0
          ? parseFloat(product.plans[0].unit_amount / 100)
          : null)
      );
    },
    buildCouponTitle(coupon) {
      return coupon.amount_off
        ? '$' + parseFloat(coupon.amount_off / 100) + ' Off'
        : coupon.percent_off + '% Off';
    },
    buildInterval(interval, intervalCount) {
      if (interval === 'week') {
        return intervalCount > 1 ? `Every ${intervalCount} weeks` : 'Weekly';
      } else if (interval === 'month') {
        return intervalCount > 1 ? `Every ${intervalCount} months` : 'Monthly';
      } else if (interval === 'year') {
        return intervalCount > 1 ? `Every ${intervalCount} years` : 'Annually';
      }
    },
    removeCouponLocal(couponId) {
      this.removingCoupon = couponId;
      this.deleteTrainerCoupon({
        trainerId: this.user.trainer._id,
        couponId,
      })
        .then((res) => {
          this.removingCoupon = null;
          this.removeCoupon(couponId);
        })
        .catch((error) => {
          console.warn(error);
          this.removingCoupon = null;
        });
    },
    viewProductDetails(product) {
      this.setSelectedProduct(product);
      this.showAdminPanel('ProductDetail', null, 'md');
    },
    getSubscribers(product) {
      if (this.joinedClientsProducts && this.joinedClientsProducts.length > 0) {
        const item = this.joinedClientsProducts.find(
          (productItem) => productItem.product.id === product.id
        );
        return item && item.subscribers ? item.subscribers : [];
      }
    },
    // TODO: enable when api ready
    // viewStripeDashboard() {
    //   console.log(this.currentUser);
    //   if (this.currentUser.roles && this.currentUser.roles.isTrainer) {
    //     // const trainer = this.currentUser.trainer;
    //     // const trainerStripeUrl = trainer
    //     // window.open(trainerStripeUrl)
    //   }
    // },
  },
  watch: {
    async isAdminPanelActive(val) {
      if (!val) {
        await this.setPanelContent(null);
        await this.setSelectedProduct(null);
      }
    },
  },
};
</script>

<template>
  <UseLargeScreen v-if="isSmallScreenView" />
  <Layout v-else>
    <AdminPanel
      :title="panelContent ? panelContent.title : ''"
      :size="panelContent ? panelContent.size : ''"
    >
      <component
        v-if="panelContent"
        :is="panelContent.component"
        :content="panelContent"
        :subscribers="selectedProduct ? getSubscribers(selectedProduct) : []"
        :user="user"
      />
    </AdminPanel>
    <div class="container-wrapper full settings">
      <div class="row">
        <div class="main-cta col-4 ml-auto">
          <!-- TODO: enable when api ready -->
          <!-- <BaseButton
            type="button"
            class="btn-secondary btn-round"
            @click="viewStripeDashboard"
          >
            View Billing Dashboard
          </BaseButton> -->
          <!-- TODO: enable when design ready -->
          <!-- <BaseButton
            type="button"
            class="btn-primary btn-round"
            @click="showAdminPanel('CouponForm', null, 'md')"
          >
            Create New Coupon
          </BaseButton> -->
        </div>
      </div>
      <div class="content-container row">
        <div class="col">
          <div class="col">
            <CheckoutSummaryBlock
              :activeProducts="activeProducts"
              :totalClients="totalClients"
              :monthsActive="monthsActive"
              :totalEarning="totalEarning"
              :totalWeeksPaid="totalWeeksPaid"
              :stripeLink="user.trainer.stripeLoginLink"
            />
          </div>
          <div
            class="col"
            v-if="user.trainer.myProducts && user.trainer.myProducts.length > 0"
          >
            <div class="summary-block-container">
              <span v-if="activeProducts && activeProducts > 0" class="title"
                >Active Products</span
              >
              <div
                class="cards-row row"
                v-for="(products, i) in groupedProducts"
                :key="i"
              >
                <template v-for="product in products">
                  <EmptyCardProductItem
                    class="col-3"
                    v-if="product.id === 'isNew'"
                    :key="product.id"
                  />
                  <CardProductItem
                    class="col-3"
                    v-if="product.id !== 'isNew'"
                    :key="product.id"
                    :product="product"
                    @view-product-details="viewProductDetails"
                    :subscribers="getSubscribers(product)"
                  />
                </template>
                <template v-for="blankCard in blankCards">
                  <div class="blank" :key="blankCard._id" />
                </template>
              </div>
            </div>
          </div>
          <div class="row" v-else>
            <div class="col">
              <p>No Products Defined</p>
            </div>
          </div>
        </div>
      </div>

      <!-- TODO: enable when design ready -->
      <!-- <div class="row">
        <div class="col">
          <h3>My Coupons</h3>
          <div
            class="row"
            v-if="user.trainer.myCoupons && user.trainer.myCoupons.length > 0"
          >
            <div
              class="col"
              v-for="localCoupon in user.trainer.myCoupons"
              :key="localCoupon.id"
            >
              <b-card
                img-src="https://picsum.photos/600/300/?image=25"
                img-alt="Image"
                img-top
                tag="article"
                style="max-width: 20rem;"
                class="mb-2"
              >
                <template v-slot:header>
                  <h4 class="mb-0">{{ localCoupon.name }}</h4>
                </template>
                <b-card-body>
                  <b-card-sub-title class="mb-2">{{
                    buildCouponTitle(localCoupon)
                  }}</b-card-sub-title>
                  <b-button
                    :key="localCoupon.id"
                    :disabled="
                      !empty(removingCoupon) &&
                        removingCoupon === localCoupon.id
                    "
                    @click.stop="removeCouponLocal(localCoupon.id)"
                    variant="danger"
                    >{{
                      !empty(removingCoupon) &&
                      removingCoupon === localCoupon.id
                        ? 'Removing...'
                        : 'Remove'
                    }}</b-button
                  >
                </b-card-body>
              </b-card>
            </div>
          </div>
          <div class="row" v-else>
            <div class="col">
              <p>No Coupons Defined</p>
            </div>
          </div>
        </div>
      </div> -->
    </div>
  </Layout>
</template>

<style lang="scss" scoped>
@import '@design';
.title {
  color: $color-black-one;
  font-size: 18px;
  font-style: normal;
  font-weight: bold;
  letter-spacing: -0.466667px;
  line-height: 32px;
  margin-bottom: 2rem;
}
// stylelint-disable selector-pseudo-element-no-unknown
.main-cta {
  margin-top: 2%;
}
.settings {
  background-color: $color-off-white;
  margin-top: 4rem;
  padding: 2.4rem 2.4rem;

  .summary-block-container {
    height: 100%;
    margin-top: 6.5rem;
    position: relative;
    .title {
      color: $color-black-one;
      font-size: 18px;
      font-style: normal;
      font-weight: bold;
      letter-spacing: -0.466667px;
      line-height: 32px;
      margin-bottom: 2rem;
    }
    .cards-row {
      height: 100%;
      margin-left: 0;
      margin-top: 1%;
      position: relative;
    }
  }

  .ml-auto {
    align-items: flex-end;
    display: flex;
    justify-content: flex-end;
    .icon-save {
      height: 25px;
    }
    .btn-primary {
      padding: 0.8rem 2.4rem;
    }
  }
  .blank {
    background-color: white;
    border-radius: 8px;
    flex: 0 0 23%;
    height: 30vh;
    margin: 0 2% 0 0;
    max-height: 30vh;
    max-width: 23%;
    min-height: 28rem;
    opacity: 0.4;
  }
}

::v-deep .panel-overlay {
  background-color: transparent !important;
}
</style>
